/**
 * BestOddsFilterBar - Compact filter bar above the Best Odds radar grid.
 *
 * Always visible: shown count, search, min-spread chips, max-odds chips, active chips.
 * Collapsible "Filters" panel: time window, bookmakers, championships, markets.
 * Instant client-side apply — no Apply button.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useBestOdds } from '../context/BestOddsContext';
import { useTranslation } from '../../i18n/hooks';
import { SPREAD_CHIP_VALUES, MAX_ODDS_CHIP_VALUES } from '../utils/bestOddsFilters';
import BestOddsBookmakerFilter from './BestOddsBookmakerFilter';
import BestOddsChampionshipFilter from './BestOddsChampionshipFilter';
import BestOddsMarketFilter from './BestOddsMarketFilter';
import BestOddsTimeWindowFilter from './BestOddsTimeWindowFilter';
import BestOddsActiveFilterChips from './BestOddsActiveFilterChips';
import './BestOddsFilterBar.css';

const SEARCH_DEBOUNCE_MS = 250;
const FILTERS_PANEL_ID = 'best-odds-filters-panel';

const BestOddsFilterBar = ({ className = '', children = null }) => {
  const { t } = useTranslation();
  const {
    filters,
    filteredCount,
    totalAnomalies,
    hasActiveFilters,
    setMinSpread,
    setMaxOdds,
    setSearchQuery
  } = useBestOdds();

  const [searchInput, setSearchInput] = useState(() => filters?.searchQuery || '');
  // Advanced filters panel starts collapsed; toggle stays always visible.
  const [panelOpen, setPanelOpen] = useState(false);
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    const nextQuery = filters?.searchQuery || '';
    setSearchInput(nextQuery);

    // Cancel pending debounce when search is cleared externally (reset / chip remove).
    if (!nextQuery && searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  }, [filters?.searchQuery]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const activeMinSpread = Number(filters?.minSpread) > 0
    ? Number(filters.minSpread)
    : 0;

  const activeMaxOdds = Number(filters?.maxOdds) > 0
    ? Number(filters.maxOdds)
    : 0;

  const handleSpreadChipClick = useCallback((value) => {
    setMinSpread(value);
  }, [setMinSpread]);

  const handleMaxOddsChipClick = useCallback((value) => {
    setMaxOdds(value);
  }, [setMaxOdds]);

  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    setSearchInput(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, SEARCH_DEBOUNCE_MS);
  }, [setSearchQuery]);

  const handleClearSearch = useCallback(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setSearchInput('');
    setSearchQuery('');
  }, [setSearchQuery]);

  const handlePanelToggle = useCallback(() => {
    setPanelOpen((prev) => !prev);
  }, []);

  const hasSearchText = searchInput.trim().length > 0;
  const panelHasRestrictedFilters = Array.isArray(filters?.selectedBookmakers)
    || Array.isArray(filters?.selectedChampionships)
    || Array.isArray(filters?.selectedMarketKeys)
    || (filters?.timeWindow && filters.timeWindow !== 'all');

  return (
    <div
      className={`best-odds-filter-bar ${hasActiveFilters ? 'best-odds-filter-bar--active' : ''} ${className}`.trim()}
      role="region"
      aria-label={t('bestOdds.filters.activeFilters')}
    >
      <div className="best-odds-filter-bar__meta">
        <span className="best-odds-filter-bar__count" aria-live="polite">
          {t('bestOdds.filters.shownCount', {
            filtered: filteredCount,
            total: totalAnomalies
          })}
        </span>
      </div>

      <div className="best-odds-filter-bar__controls">
        <div className="best-odds-filter-bar__search">
          <label className="best-odds-filter-bar__search-label visually-hidden" htmlFor="best-odds-search">
            {t('bestOdds.filters.searchAriaLabel')}
          </label>
          <input
            id="best-odds-search"
            type="text"
            className="best-odds-filter-bar__search-input"
            value={searchInput}
            onChange={handleSearchChange}
            placeholder={t('bestOdds.filters.searchPlaceholder')}
            aria-label={t('bestOdds.filters.searchAriaLabel')}
            autoComplete="off"
          />
          {hasSearchText && (
            <button
              type="button"
              className="best-odds-filter-bar__search-clear"
              onClick={handleClearSearch}
              aria-label={t('bestOdds.filters.searchClearAriaLabel')}
              title={t('bestOdds.filters.searchClear')}
            >
              ×
            </button>
          )}
        </div>

        <div className="best-odds-filter-bar__chip-row">
          <span className="best-odds-filter-bar__chip-row-label" id="best-odds-spread-label">
            {t('bestOdds.filters.spreadLabel')}
          </span>
          <div
            className="best-odds-filter-bar__chip-row-chips"
            role="group"
            aria-labelledby="best-odds-spread-label"
          >
            {SPREAD_CHIP_VALUES.map((value) => {
              const isAll = value === 0;
              const isActive = activeMinSpread === value;
              const label = isAll
                ? t('bestOdds.filters.spreadAll')
                : t('bestOdds.filters.spreadChip', { value });
              const ariaLabel = isAll
                ? t('bestOdds.filters.spreadAllAriaLabel')
                : t('bestOdds.filters.spreadChipAriaLabel', { value });

              return (
                <button
                  key={value}
                  type="button"
                  className={`best-odds-filter-bar__chip${isActive ? ' is-active' : ''}`}
                  aria-pressed={isActive}
                  aria-label={ariaLabel}
                  onClick={() => handleSpreadChipClick(value)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="best-odds-filter-bar__chip-row">
          <span className="best-odds-filter-bar__chip-row-label" id="best-odds-max-odds-label">
            {t('bestOdds.filters.maxOddsLabel')}
          </span>
          <div
            className="best-odds-filter-bar__chip-row-chips"
            role="group"
            aria-labelledby="best-odds-max-odds-label"
          >
            {MAX_ODDS_CHIP_VALUES.map((value) => {
              const isAll = value === 0;
              const isActive = activeMaxOdds === value;
              const label = isAll
                ? t('bestOdds.filters.maxOddsAll')
                : t('bestOdds.filters.maxOddsChip', { value });
              const ariaLabel = isAll
                ? t('bestOdds.filters.maxOddsAllAriaLabel')
                : t('bestOdds.filters.maxOddsChipAriaLabel', { value });

              return (
                <button
                  key={value}
                  type="button"
                  className={`best-odds-filter-bar__chip${isActive ? ' is-active' : ''}`}
                  aria-pressed={isActive}
                  aria-label={ariaLabel}
                  onClick={() => handleMaxOddsChipClick(value)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <button
        type="button"
        className={`best-odds-filter-bar__panel-toggle${panelOpen ? ' is-open' : ''}${panelHasRestrictedFilters ? ' has-panel-filters' : ''}`}
        onClick={handlePanelToggle}
        aria-expanded={panelOpen}
        aria-controls={FILTERS_PANEL_ID}
      >
        <span className="best-odds-filter-bar__panel-toggle-text">
          {panelOpen
            ? t('bestOdds.filters.panelToggleHide')
            : t('bestOdds.filters.panelToggleShow')}
          {panelHasRestrictedFilters && (
            <span className="best-odds-filter-bar__panel-toggle-dot" aria-hidden="true" />
          )}
        </span>
        <span className="best-odds-filter-bar__panel-toggle-chevron" aria-hidden="true">
          ▾
        </span>
      </button>

      {panelOpen && (
        <div
          id={FILTERS_PANEL_ID}
          className="best-odds-filter-bar__panel"
          role="region"
          aria-label={t('bestOdds.filters.panelAriaLabel')}
        >
          <BestOddsTimeWindowFilter />
          <BestOddsBookmakerFilter showSectionLabel />
          <BestOddsChampionshipFilter />
          <BestOddsMarketFilter />
          {children}
        </div>
      )}

      <BestOddsActiveFilterChips />
    </div>
  );
};

BestOddsFilterBar.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};

export default BestOddsFilterBar;
